import React, { FunctionComponent, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

import Text from 'common/components/Text/Text';
import { generateWordDocument, generatePdf } from 'common/utils';

import styles from './PersonalStatementHub.module.scss';
import { HeaderIconLeft, HeaderIconRight } from 'assets/personal_statement_builder';
import {
  useDuplicatePersonalStatementMutation,
  useGetPersonalStatementsQuery,
  usePatchPersonalStatementMutation,
  useDeletePersonalStatementMutation,
  useLazyGetPostulationsListQuery,
} from 'services/exploreApi';
import { CommonHash, PersonalStatement, Postulation } from 'app/types';
import TagManager from 'react-gtm-module';
import { PostulationItemComponent } from './PersonalStatementBuilder';

interface PersonalStatementCardProps {
  personalStatement: PersonalStatement;
  nowDateTime: Date;
}

const PersonalStatementCard = ({
  personalStatement,
  nowDateTime,
}: PersonalStatementCardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [showApplicationModal, setShowApplicationModal] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>(personalStatement.formatted_name);
  const [warningAdvice, setWarningAdvice] = useState<string>('');
  const [duplicatePersonalStatement] = useDuplicatePersonalStatementMutation();
  const [deletePersonalStatement] = useDeletePersonalStatementMutation();

  const [patchPersonalStatement, { data: updatedPersonalStatement, isLoading: isPatchLoading }] =
    usePatchPersonalStatementMutation();

  const [
    getPostulations,
    { data: postulations, isLoading: postulationsLoading, isFetching: postulationsFetching },
  ] = useLazyGetPostulationsListQuery();

  const personalStatementGenerable = () => {
    return (
      personalStatement.gpt_response &&
      personalStatement.gpt_response.length > 0 &&
      personalStatement.status === 'generated'
    );
  };

  const sendGTMEvent = (eventName: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: eventName,
      },
    });
  };

  const getDifferenceDates = (updatedDate: string) => {
    const difference = nowDateTime.getTime() - new Date(updatedDate).getTime();
    const diffDays = Math.floor(difference / 86400000); // days
    const diffHrs = Math.floor((difference % 86400000) / 3600000); // hours
    const diffMins = Math.round(((difference % 86400000) % 3600000) / 60000); // minutes
    if (diffDays > 0) {
      return `Edited ${diffDays} days ago`;
    } else if (diffHrs > 0) {
      return `Edited ${diffHrs} hours ago`;
    } else {
      return `Edited ${diffMins} minutes ago`;
    }
  };

  const handleDuplicatePersonalStatement = () => {
    duplicatePersonalStatement(personalStatement.id).then((response: any) => {
      sendGTMEvent('ps_builder_duplicate');
    });
  };

  const handleDeletePersonalStatement = () => {
    deletePersonalStatement(personalStatement.id).then((response: any) => {
      console.log(response);
    });
  };

  const handleBookmarkPersonalStatement = () => {
    const values: CommonHash = {
      personal_statement: {
        bookmarked: !personalStatement.bookmarked,
        personal_statement_answers_attributes: [],
      },
    };
    patchPersonalStatement({ data: values, id: personalStatement.id }).then((response: any) => {
      console.log(response);
    });
  };

  const handleRenamePersonalStatement = () => {
    if (newName.length > 0 && newName !== personalStatement.formatted_name) {
      const values: CommonHash = {
        personal_statement: {
          name: newName,
          personal_statement_answers_attributes: [],
        },
      };
      patchPersonalStatement({ data: values, id: personalStatement.id }).then((response: any) => {
        setShowRenameModal(false);
      });
    } else {
      setWarningAdvice('You must add a new name to edit the existing one.');
    }
  };

  const handleReturnToForm = () => {
    navigate(`/personal-statements/${personalStatement.id}`);
    sendGTMEvent('ps_builder_return_to_form');
  };

  const handleShowApplicationModal = () => {
    setShowApplicationModal(true);
    getPostulations();
  };

  return (
    <div className={`card h-100 ${styles.personalStatementContainer}`}>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className={styles.modalTitle}>
            {personalStatement.formatted_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>Do you want to delete this personal statement from your Hub?</Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Close
          </Button>
          <Button variant="danger" onClick={handleDeletePersonalStatement}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showRenameModal}
        onHide={() => {
          setShowRenameModal(false);
          setWarningAdvice('');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className={styles.modalTitle}>
            {personalStatement.formatted_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            className={`form-control`}
            type="text"
            onChange={(e) => {
              setNewName(e.target.value);
            }}
            value={newName}
            placeholder="New personal statement name"
          />
          {warningAdvice && <div className={styles.warningLabel}>{warningAdvice}</div>}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="secondary" onClick={() => setShowRenameModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleRenamePersonalStatement}>
            Edit name
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className={styles.modalTitle}>
            {personalStatement.formatted_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>Download as</Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="primary"
            onClick={() =>
              generatePdf(
                personalStatement.gpt_response,
                personalStatement.formatted_name,
                sendGTMEvent,
                setShowDownloadModal
              )
            }
          >
            PDF
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              generateWordDocument(
                personalStatement.gpt_response,
                personalStatement.formatted_name,
                sendGTMEvent,
                undefined,
                true
              );
              setShowDownloadModal(false);
            }}
          >
            Word Document
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showApplicationModal}
        onHide={() => {
          setShowApplicationModal(false);
        }}
        // dialogClassName="modal-80w"
        size="lg"
      >
        <Modal.Body>Add to application</Modal.Body>
        <Modal.Footer className={styles.modalFooterApplications}>
          {postulations?.data.length > 0 &&
            postulations?.data.map(
              (postulation: Postulation, index: number) =>
                personalStatement.gpt_response && (
                  <PostulationItemComponent
                    postulation={postulation}
                    index={index}
                    key={postulation.id}
                    responseGpt={personalStatement.gpt_response}
                    psName={personalStatement.formatted_name}
                    sendGTMEvent={sendGTMEvent}
                    setShowApplicationModal={setShowApplicationModal}
                  />
                )
            )}
        </Modal.Footer>
      </Modal>
      <div className={styles.personalStatementContent} onClick={handleReturnToForm}>
        {personalStatement.bookmarked && (
          <i className={`bi bi-bookmark-fill ${styles.bookmarkIcon}`}></i>
        )}
        {personalStatement.status === 'pending' && (
          <div className="d-flex flex-row justify-content-center align-items-center h-100 w-100">
            <button className={`btn btn-primary btn-lg ${styles.editButton}`}>
              Continue editing
            </button>
          </div>
        )}
        {personalStatement.status === 'generated' && (
          <Text
            size={14}
            weight={400}
            inlineStyle={{
              whiteSpace: 'pre-line',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {personalStatement.gpt_response}
          </Text>
        )}
      </div>
      <div className={styles.personalStatementInfo}>{personalStatement.formatted_name}</div>
      <div className="d-flex justify-content-between align-items-center">
        <div className={styles.personalStatementDate}>
          {getDifferenceDates(personalStatement.updated_at.toString())}
        </div>
        <Dropdown className={styles.personalStatementDropdown}>
          <Dropdown.Toggle>
            <i className={`bi bi-three-dots-vertical ${styles.dropdownToggleIcon}`}></i>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item className={styles.dropdownItem} onClick={() => setShowRenameModal(true)}>
              <i className="bi bi-fonts"></i>
              <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
                Rename
              </Text>
            </Dropdown.Item>
            <Dropdown.Item className={styles.dropdownItem} onClick={handleReturnToForm}>
              <i className="bi bi-pencil"></i>
              <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
                Edit answers
              </Text>
            </Dropdown.Item>
            <Dropdown.Item
              className={styles.dropdownItem}
              onClick={handleDuplicatePersonalStatement}
            >
              <i className="bi bi-files"></i>
              <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
                Duplicate
              </Text>
            </Dropdown.Item>
            {personalStatementGenerable() && (
              <Dropdown.Item className={styles.dropdownItem} onClick={handleShowApplicationModal}>
                <i className="bi bi-file-earmark-plus"></i>
                <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
                  Add to application
                </Text>
              </Dropdown.Item>
            )}
            <Dropdown.Item
              className={styles.dropdownItem}
              onClick={handleBookmarkPersonalStatement}
            >
              <i className="bi bi-bookmark"></i>
              <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
                Bookmark
              </Text>
            </Dropdown.Item>
            <Dropdown.Item className={styles.dropdownItem} onClick={() => setShowDeleteModal(true)}>
              <i className="bi bi-trash"></i>
              <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
                Delete
              </Text>
            </Dropdown.Item>
            {personalStatementGenerable() && (
              <Dropdown.Item
                className={styles.dropdownItem}
                onClick={() => setShowDownloadModal(true)}
              >
                <i className="bi bi-download"></i>
                <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
                  Download
                </Text>
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

const PersonalStatementHub: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data: personalStatements, isLoading, isFetching } = useGetPersonalStatementsQuery();
  const [availableToCreate, setAvailableToCreate] = useState<boolean>(
    personalStatements?.data.length && personalStatements.data.length > 29 ? false : true
  );
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [nowDateTime, setNowDateTime] = useState<Date>(new Date());

  setInterval(() => setNowDateTime(new Date()), 60000);

  useEffect(() => {
    setAvailableToCreate(
      (personalStatements?.data.length && personalStatements.data.length > 29) ||
        (personalStatements?.data.filter((ps) => ps.status === 'pending').length &&
          personalStatements.data.filter((ps) => ps.status === 'pending').length > 2)
        ? false
        : true
    );
    if (personalStatements?.data.length && personalStatements.data.length > 29) {
      setWarningMessage('You already have 30 personal statements');
    } else if (
      personalStatements?.data.filter((ps) => ps.status === 'pending').length &&
      personalStatements.data.filter((ps) => ps.status === 'pending').length > 2
    ) {
      setWarningMessage('You already have 3 pending personal statements');
    }
  }, [personalStatements]);

  return (
    <div className={styles.hubContainer}>
      <div className={styles.headerContainer}>
        <img src={HeaderIconLeft} className={styles.backgroundIconLeft}></img>
        <div className={styles.infoContainer}>
          <Text size={30} weight={700} inlineStyle={{ color: '#193676' }}>
            Personal Statement Hub
          </Text>
          <Text size={16} weight={400} inlineStyle={{ color: '#727D9B' }}>
            Generate a new personal statement or edit an existing one.
          </Text>
        </div>
        {availableToCreate ? (
          <button
            className={`btn btn-primary btn-lg ${styles.createButton}`}
            onClick={() => navigate('/personal-statement-builder')}
          >
            <i className="bi bi-pencil-fill"></i>
            New personal statement
          </button>
        ) : (
          <div className="d-flex flex-column align-items-center gap-1">
            <button className={`btn btn-primary btn-lg ${styles.disabledCreateButton}`}>
              <i className="bi bi-pencil-fill"></i>
              New personal statement
            </button>
            <div className={styles.warningLabel}>{warningMessage}</div>
          </div>
        )}
        <img src={HeaderIconRight} className={styles.backgroundIconRight}></img>
      </div>
      <div className={styles.hubContent}>
        <div className={`${styles.personalStatementsContainer} row row-cols-1 row-cols-lg-4`}>
          {personalStatements?.data.map((personalStatement) => (
            <div className={styles.personalStatement} key={personalStatement.id}>
              <PersonalStatementCard
                personalStatement={personalStatement}
                nowDateTime={nowDateTime}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PersonalStatementHub;
